import React from "react";
import { IconArrowLeft, IconArrowRight } from "./icons";

const CarouselContext = React.createContext(null);

const useCarousel = () => {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
};

const Carousel = React.forwardRef(({ setApi, className, children, opts, plugins, orientation, ...props }, ref) => {
  const viewportRef = React.useRef(null);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const loopEnabled = Boolean(opts?.loop);

  const getSlideMetrics = React.useCallback((viewport) => {
    const { scrollWidth, clientWidth } = viewport;
    const hasOverflow = scrollWidth > clientWidth + 1;
    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);

    return { hasOverflow, maxScrollLeft };
  }, []);

  const getScrollStep = React.useCallback((viewport) => {
    const slides = viewport.querySelectorAll(".carousel-slide");
    const firstSlide = slides[0];
    if (!firstSlide) {
      return viewport.clientWidth;
    }

    if (slides.length > 1) {
      const firstRect = firstSlide.getBoundingClientRect();
      const secondRect = slides[1].getBoundingClientRect();
      const measuredStep = Math.abs(secondRect.left - firstRect.left);
      if (measuredStep > 0) {
        return measuredStep;
      }
    }

    const slideWidth = firstSlide.getBoundingClientRect().width;
    return slideWidth || viewport.clientWidth;
  }, []);

  const updateScrollState = React.useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = viewport;
    const { hasOverflow } = getSlideMetrics(viewport);

    if (loopEnabled && hasOverflow) {
      setCanScrollPrev(true);
      setCanScrollNext(true);
      return;
    }

    setCanScrollPrev(scrollLeft > 0);
    setCanScrollNext(scrollLeft + clientWidth < scrollWidth - 1);
  }, [getSlideMetrics, loopEnabled]);

  const scrollPrev = React.useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const { scrollLeft } = viewport;
    const { hasOverflow, maxScrollLeft } = getSlideMetrics(viewport);
    const scrollStep = getScrollStep(viewport);

    if (loopEnabled && hasOverflow && scrollLeft <= 1) {
      viewport.scrollTo({ left: maxScrollLeft, behavior: "smooth" });
      return;
    }

    viewport.scrollBy({ left: -scrollStep, behavior: "smooth" });
  }, [getScrollStep, getSlideMetrics, loopEnabled]);

  const scrollNext = React.useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const { scrollLeft } = viewport;
    const { hasOverflow, maxScrollLeft } = getSlideMetrics(viewport);
    const scrollStep = getScrollStep(viewport);

    if (loopEnabled && hasOverflow && scrollLeft >= maxScrollLeft - 1) {
      viewport.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }

    viewport.scrollBy({ left: scrollStep, behavior: "smooth" });
  }, [getScrollStep, getSlideMetrics, loopEnabled]);

  const scrollToStart = React.useCallback((behavior = "auto") => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    viewport.scrollTo({ left: 0, behavior });
  }, []);

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    viewport.scrollTo({ left: 0, behavior: "auto" });
  }, []);

  React.useEffect(() => {
    if (!setApi) {
      return;
    }

    setApi({ scrollPrev, scrollNext, scrollToStart });
  }, [setApi, scrollPrev, scrollNext, scrollToStart]);

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const handleScroll = () => updateScrollState();

    updateScrollState();
    viewport.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      viewport.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  return (
    <CarouselContext.Provider value={{ viewportRef, scrollPrev, scrollNext, canScrollPrev, canScrollNext }}>
      <div
        ref={ref}
        className={className ? `relative ${className}` : "relative"}
        role="region"
        aria-roledescription="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
});
Carousel.displayName = "Carousel";

const CarouselContent = React.forwardRef(({ className, ...props }, ref) => {
  const { viewportRef } = useCarousel();

  return (
    <div ref={viewportRef} className="carousel-viewport">
      <div ref={ref} className={className ? `flex ${className}` : "flex"} {...props} />
    </div>
  );
});
CarouselContent.displayName = "CarouselContent";

const CarouselItem = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    role="group"
    aria-roledescription="slide"
    className={className ? `carousel-slide min-w-0 shrink-0 grow-0 basis-full pl-4 ${className}` : "carousel-slide min-w-0 shrink-0 grow-0 basis-full pl-4"}
    {...props}
  />
));
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = React.forwardRef(({ className, ...props }, ref) => {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <button
      ref={ref}
      type="button"
      className={
        className
          ? `absolute h-8 w-8 rounded-full border border-border bg-card text-muted-foreground flex items-center justify-center transition-colors hover:text-foreground ${className}`
          : "absolute h-8 w-8 rounded-full border border-border bg-card text-muted-foreground flex items-center justify-center transition-colors hover:text-foreground"
      }
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <IconArrowLeft className="h-4 w-4" />
      <span className="sr-only">Previous slide</span>
    </button>
  );
});
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = React.forwardRef(({ className, ...props }, ref) => {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <button
      ref={ref}
      type="button"
      className={
        className
          ? `absolute h-8 w-8 rounded-full border border-border bg-card text-muted-foreground flex items-center justify-center transition-colors hover:text-foreground ${className}`
          : "absolute h-8 w-8 rounded-full border border-border bg-card text-muted-foreground flex items-center justify-center transition-colors hover:text-foreground"
      }
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <IconArrowRight className="h-4 w-4" />
      <span className="sr-only">Next slide</span>
    </button>
  );
});
CarouselNext.displayName = "CarouselNext";

export { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext };
